import React, { useState, useEffect, useRef } from "react"
import { Bars3Icon, ExclamationTriangleIcon, CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import { useFieldDragController } from "aldoo-ra/CMS/controllers/field-drag-controller"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import { validateProductIdUniqueness } from "../validators/product-id-validator"
import { showNotification } from "aldoo-ra/Notification"
import _ from "lodash"

function PlatformProductEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
}) {
  const [localValue, setLocalValue] = useState(value ?? [])
  const [showDialog, setShowDialog] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [itemContentValues, setItemContentValues] = useState({})
  const [validationWarnings, setValidationWarnings] = useState({})
  const [validatingFields, setValidatingFields] = useState({})
  const [dialogValidationWarning, setDialogValidationWarning] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [hasAutoOpened, setHasAutoOpened] = useState(false)
  const isInternalChange = useRef(false)
  const productIdInputRef = useRef(null)
  const dialogValidatorRef = useRef(null)

  const labelField = field?.item?.labelField || "platform"

  // Helper function to collect ALL platform product IDs from ALL price models
  const getAllPlatformProductIds = () => {
    // The hierarchy is:
    // - parentObject is the StorePrice document with price_model array
    // - Each item in price_model has a real_product_id array
    // We need to collect ALL real_product_id items from ALL price_model items

    let priceModelArray = []

    // If parentValues is a function, call it to get current form state
    let parentValuesData = null
    if (typeof parentValues === 'function') {
      parentValuesData = parentValues()
    } else {
      parentValuesData = parentValues
    }

    // Get parentObject data (handle both object and function)
    const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject

    // Try to find the price_model array - check current form state first, then fallback to parentObject
    if (parentValuesData?.price_model && Array.isArray(parentValuesData.price_model)) {
      priceModelArray = parentValuesData.price_model
    } else if (parentValuesData?.price?.price_model && Array.isArray(parentValuesData.price.price_model)) {
      priceModelArray = parentValuesData.price.price_model
    } else if (parentObjectData?.price_model && Array.isArray(parentObjectData.price_model)) {
      priceModelArray = parentObjectData.price_model
    }

    // Collect all platform product IDs from all price models
    const allPlatformIds = []
    for (const priceModel of priceModelArray) {
      if (priceModel.real_product_id && Array.isArray(priceModel.real_product_id)) {
        for (const productId of priceModel.real_product_id) {
          allPlatformIds.push({
            platform: typeof productId.platform === 'object' ? productId.platform.value : productId.platform,
            id: productId.id
          })
        }
      }
    }

    return allPlatformIds
  }

  // Helper function to get the price ID for exclusion
  const getValidationExclusionOptions = () => {
    // Try to find the price ID from the parent context
    // The parent could be the StorePrice object itself, or a StoreProduct containing price
    let priceId = null

    // Get parentObject data (handle both object and function)
    const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject

    // When editing price directly (from price button), parentValues IS the price object
    if (parentValues?._id && !parentValues?.price) {
      priceId = parentValues._id
    }
    // When editing within a product, parentValues has price field
    else if (parentValues?.price?._id) {
      priceId = parentValues.price._id
    }
    // parentObject might be a price model within the array
    else if (parentObjectData?._id) {
      priceId = parentObjectData._id
    }

    return priceId ? { excludePriceId: priceId } : {}
  }

  // Debounced validator instances - one per item
  const debouncedValidators = useRef({})
  
  const createDebouncedValidator = (index) => {
    if (!debouncedValidators.current[index]) {
      debouncedValidators.current[index] = _.debounce(async (productId, platform, currentItems) => {
      if (!productId || !productId.trim()) {
        setValidationWarnings(prev => {
          const newWarnings = { ...prev }
          delete newWarnings[`${index}_id`]
          return newWarnings
        })
        setValidatingFields(prev => {
          const newFields = { ...prev }
          delete newFields[`${index}_id`]
          return newFields
        })
        return
      }

      // Get the appropriate exclusion options based on parent context
      const validationOptions = getValidationExclusionOptions()

      // Add ALL platform product IDs from ALL price models to validation
      const allPlatformIds = getAllPlatformProductIds()
      validationOptions.currentProductIds = allPlatformIds

      const result = await validateProductIdUniqueness(productId.trim(), platform, validationOptions)

      setValidatingFields(prev => {
        const newFields = { ...prev }
        delete newFields[`${index}_id`]
        return newFields
      })

      if (!result.isValid && result.duplicateProduct) {
        setValidationWarnings(prev => {
          const newWarnings = {
            ...prev,
            [`${index}_id`]: {
              message: result.internalDuplicate
                ? `This Product ID is duplicated within this product`
                : `Duplicate Product ID`,
              duplicateProduct: result.duplicateProduct,
              conflictingProducts: result.conflictingProducts || [],
              internalDuplicate: result.internalDuplicate || false,
              type: 'warning'
            }
          }
          return newWarnings
        })
      } else {
        setValidationWarnings(prev => {
          const newWarnings = { ...prev }
          delete newWarnings[`${index}_id`]
          return newWarnings
        })
      }

      if (result.error) {
        console.warn('ProductId validation warning:', result.error)
      }
    }, 800)
    }
    return debouncedValidators.current[index]
  }

  useEffect(() => {
    return () => {
      // Cancel all debounced validators when component unmounts
      Object.values(debouncedValidators.current).forEach(validator => validator?.cancel())
    }
  }, [])

  

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObjectData)
      : field.required

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      const newValue = value ?? []
      setLocalValue(newValue)
    }
    isInternalChange.current = false
  }, [value])

  // Auto-open disabled - user can manually add platform products when needed
  // useEffect(() => {
  //   console.log('🎯 PlatformProductEditor auto-open check:', {
  //     localValueLength: localValue.length,
  //     hasAutoOpened,
  //     showDialog,
  //     value: value
  //   })

  //   // Only auto-open if:
  //   // 1. Array is empty
  //   // 2. Haven't already auto-opened
  //   // 3. Dialog is not already open
  //   if (localValue.length === 0 && !hasAutoOpened && !showDialog) {
  //     console.log('✅ Auto-opening dialog for empty platform products')
  //     setHasAutoOpened(true)
  //     // Use a small timeout to ensure component is fully mounted
  //     setTimeout(() => {
  //       addNewInput()
  //     }, 50)
  //   }
  // }, [localValue.length, hasAutoOpened, showDialog])

  // Validate productIds when items change - check for local duplicates and trigger database checks
  useEffect(() => {
    const newWarnings = {}
    const newValidatingFields = {}

    localValue.forEach((item, index) => {
      if (item.id && item.platform) {
        // First check for local duplicates within the current form
        const currentPlatform = typeof item.platform === 'object' ? item.platform.value : item.platform
        const localDuplicates = localValue.filter((otherItem, otherIndex) => {
          const otherPlatform = typeof otherItem.platform === 'object' ? otherItem.platform.value : otherItem.platform
          return otherIndex !== index &&
            otherPlatform === currentPlatform &&
            otherItem.id === item.id
        })

        if (localDuplicates.length > 0) {
          // Get parent product PID for the warning
          const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
          const parentProductPid = parentValues?._parentProduct?.pid ||
                                  parentValues?.pid ||
                                  parentObjectData?._parentProduct?.pid ||
                                  null

          newWarnings[`${index}_id`] = {
            message: `This Product ID is duplicated within this product`,
            internalDuplicate: true,
            conflictingProducts: parentProductPid ? [parentProductPid] : [],
            type: 'warning'
          }
        } else {
          // No local duplicate, check database
          newValidatingFields[`${index}_id`] = true

          const validator = createDebouncedValidator(index)
          validator(item.id, currentPlatform, localValue)
        }
      }
    })

    setValidationWarnings(newWarnings)
    setValidatingFields(newValidatingFields)
  }, [JSON.stringify(localValue), parentObject]) // Deep comparison to catch platform changes

  const addNewInput = () => {
    // Check if there's an existing item for iOS (default platform)
    const existingIOSItem = localValue.find(item =>
      (typeof item.platform === 'object' ? item.platform.value : item.platform) === "iOS"
    )

    const newItem = existingIOSItem
      ? { ...existingIOSItem, platform: "iOS" }
      : { platform: "iOS", id: "" }

    setSelectedIndex(null) // null means adding new
    setItemContentValues(newItem)
    setShowDialog(true)
  }

  const removeInput = (index) => {
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
    
    // Clean up validation states
    setValidationWarnings(prev => {
      const newWarnings = { ...prev }
      delete newWarnings[`${index}_id`]
      return newWarnings
    })
    setValidatingFields(prev => {
      const newFields = { ...prev }
      delete newFields[`${index}_id`]
      return newFields
    })
  }

  const clearAll = () => {
    updateValue([])
    setValidationWarnings({})
    setValidatingFields({})
  }

  const handleEdit = (index) => {
    setSelectedIndex(index)
    const itemToEdit = localValue[index] || {}
    setItemContentValues({ ...itemToEdit })
    setShowDialog(true)
  }

  const handleCloseDialog = () => {
    setShowDialog(false)
    setSelectedIndex(null)
    setDialogValidationWarning(null)
    validateDialogProductId.cancel?.()
  }

  // Validate product ID in dialog
  const validateDialogProductId = useRef(
    _.debounce(async (productId, platform, currentLocalValue, currentSelectedIndex, parentCtx) => {
      if (!productId || !productId.trim()) {
        setDialogValidationWarning(null)
        return
      }

      // Extract platform string if it's an object (from dropdown selection)
      const platformString = typeof platform === 'object' && platform.value ? platform.value : platform

      // Create a simulated array with the current edit applied
      const simulatedValue = [...currentLocalValue]
      const currentIndex = currentSelectedIndex
      let newItemIndex = null // Track where the new item will be

      if (currentIndex !== null && currentIndex < simulatedValue.length) {
        // Editing existing item - update it with current values
        simulatedValue[currentIndex] = { platform: platformString, id: productId.trim() }
        newItemIndex = currentIndex
      } else {
        // Adding new item - append it
        newItemIndex = simulatedValue.length
        simulatedValue.push({ platform: platformString, id: productId.trim() })
      }

      // Now check for duplicates in the simulated array (excluding the item we're adding/editing)
      const localDuplicates = simulatedValue.filter((item, index) =>
        index !== newItemIndex &&
        (typeof item.platform === 'object' ? item.platform.value : item.platform) === platformString &&
        item.id === productId.trim()
      )

      if (localDuplicates.length > 0) {
        // Get parent product PID for the warning
        const parentCtxObjectData = typeof parentCtx?.parentObject === 'function' ? parentCtx.parentObject() : parentCtx?.parentObject
        const parentProductPid = parentCtx?.parentValues?._parentProduct?.pid ||
                                parentCtx?.parentValues?.pid ||
                                parentCtxObjectData?._parentProduct?.pid ||
                                null

        setDialogValidationWarning({
          message: `This Product ID is duplicated within this product`,
          internalDuplicate: true,
          conflictingProducts: parentProductPid ? [parentProductPid] : []
        })
        return
      }

      // Get the appropriate exclusion options based on parent context
      const validationOptions = parentCtx?.getValidationExclusionOptions() || {}

      // Add ALL platform product IDs from ALL price models to validation
      // We need to simulate the state AFTER the current edit is saved
      let allPlatformIds = parentCtx?.getAllPlatformProductIds() || []

      // We need to EXCLUDE items from the current localValue when checking allPlatformIds
      // because allPlatformIds includes the CURRENT state, but we're editing it
      // Remove all items from currentLocalValue from allPlatformIds
      const currentLocalValueIds = currentLocalValue.map(item => ({
        platform: typeof item.platform === 'object' ? item.platform.value : item.platform,
        id: item.id
      }))

      // Filter out items that belong to the current real_product_id array
      const externalPlatformIds = allPlatformIds.filter(extItem => {
        return !currentLocalValueIds.some(localItem =>
          localItem.platform === extItem.platform && localItem.id === extItem.id
        )
      })

      // Check if the value we're entering conflicts with ANY other platform ID
      // across OTHER price models (not the current one)

      // Count how many times the NEW value appears in externalPlatformIds
      let matchCount = 0
      for (const item of externalPlatformIds) {
        if (item.platform === platformString && item.id === productId.trim()) {
          matchCount++
        }
      }

      if (matchCount > 0) {
        const parentCtxObjectData = typeof parentCtx?.parentObject === 'function' ? parentCtx.parentObject() : parentCtx?.parentObject
        const parentProductPid = parentCtx?.parentValues?._parentProduct?.pid ||
                                parentCtx?.parentValues?.pid ||
                                parentCtxObjectData?._parentProduct?.pid ||
                                null

        setDialogValidationWarning({
          message: `This Product ID is duplicated within this product`,
          internalDuplicate: true,
          conflictingProducts: parentProductPid ? [parentProductPid] : []
        })
        return
      }

      // No conflicts found - clear warning and still do backend check for external duplicates
      validationOptions.currentProductIds = allPlatformIds

      const result = await validateProductIdUniqueness(productId.trim(), platformString, validationOptions)

      if (!result.isValid && result.duplicateProduct) {
        setDialogValidationWarning({
          message: result.internalDuplicate
            ? `This Product ID is duplicated within this product`
            : `Duplicate Product ID`,
          internalDuplicate: result.internalDuplicate || false,
          conflictingProducts: result.conflictingProducts || []
        })
      } else {
        setDialogValidationWarning(null)
      }

      if (result.error) {
        console.warn('Real-time ProductId validation warning:', result.error)
      }
    }, 800)
  ).current

  // Trigger validation when itemContentValues changes
  useEffect(() => {
    if (showDialog && itemContentValues.id && itemContentValues.platform) {
      const parentCtx = {
        parentValues,
        parentObject,
        getValidationExclusionOptions,
        getAllPlatformProductIds
      }
      validateDialogProductId(
        itemContentValues.id,
        itemContentValues.platform,
        localValue,
        selectedIndex,
        parentCtx
      )
    } else if (showDialog && !itemContentValues.id) {
      setDialogValidationWarning(null)
    }
  }, [itemContentValues.id, itemContentValues.platform, showDialog, localValue, selectedIndex])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      validateDialogProductId.cancel?.()
    }
  }, [])

  const handleSaveItem = async (newValues) => {
    // Set saving state
    setIsSaving(true)

    try {
      // Small delay to show the spinner
      await new Promise(resolve => setTimeout(resolve, 100))

      // Validate before saving - check for duplicates
      const platformString = typeof newValues.platform === 'object' ? newValues.platform.value : newValues.platform
      const productIdString = newValues.id?.trim()

      if (!productIdString) {
        showNotification({
          message: 'Product ID is required',
          type: 'error'
        })
        return false
      }

      // Check for duplicates in the final state after this save
      const updatedValues = [...localValue]
      let newItemIndex = null

      if (selectedIndex !== null && selectedIndex < updatedValues.length) {
        updatedValues[selectedIndex] = newValues
        newItemIndex = selectedIndex
      } else {
        newItemIndex = updatedValues.length
        updatedValues.push(newValues)
      }

      // Check for local duplicates within this real_product_id array (excluding the item we're saving)
      const localDuplicates = updatedValues.filter((item, index) => {
        const itemPlatform = typeof item.platform === 'object' ? item.platform.value : item.platform
        return index !== newItemIndex &&
          itemPlatform === platformString &&
          item.id === productIdString
      })

      if (localDuplicates.length > 0) {
        showNotification({
          message: 'This Product ID is already used in this price configuration',
          type: 'error'
        })
        return false
      }

      // Check for duplicates across other price models
      const allPlatformIds = getAllPlatformProductIds()
      const originalItem = selectedIndex !== null && selectedIndex < localValue.length ? localValue[selectedIndex] : null
      const originalPlatform = originalItem ? (typeof originalItem.platform === 'object' ? originalItem.platform.value : originalItem.platform) : null
      const originalId = originalItem?.id

      let matchCount = 0
      for (const item of allPlatformIds) {
        if (item.platform === platformString && item.id === productIdString) {
          matchCount++
        }
      }

      // If we're editing an existing item and it matches the original, don't count it
      if (originalPlatform === platformString && originalId === productIdString) {
        matchCount--
      }

      if (matchCount > 0) {
        showNotification({
          message: 'This Product ID is duplicated across different price configurations',
          type: 'error'
        })
        return false
      }

      // No duplicates - safe to save
      updateValue(updatedValues)

      // Validation warnings will be recalculated automatically by the effect
      return true
    } finally {
      setIsSaving(false)
    }
  }

  const renderItem = (item, index) => {
    const hasWarning = validationWarnings[`${index}_id`]
    const isValidating = validatingFields[`${index}_id`]
    const platformDisplay = getPlatformDisplayName(item.platform)
    const displayText = item.id || 'No ID'

    return (
      <div
        key={index}
        className="w-full mt-3 first:mt-0"
        draggable={localValue?.length > 1}
        onDragStart={localValue?.length > 1 ? () => handleDragStart(index) : undefined}
        onDragOver={localValue?.length > 1 ? (e) => {
          e.preventDefault()
          handleDragOver(index)
        } : undefined}
        onDragEnd={localValue?.length > 1 ? handleDragEnd : undefined}
      >
        <div
          onClick={() => handleEdit(index)}
          className={`bg-admin_paper border rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer ${
            hasWarning ? 'border-admin_warning' : 'border-admin_bordered'
          }`}
        >
          <div className="flex items-center gap-3">
            {localValue.length > 1 && (
              <Bars3Icon
                className="w-5 h-5 cursor-move text-admin_text_secondary flex-shrink-0"
                onMouseDown={(e) => e.stopPropagation()}
              />
            )}

            {/* Platform Icon */}
            <div className="flex items-center gap-2">
              {renderPlatformIcon(item.platform, "w-6 h-6 text-admin_text_secondary")}

              {/* <span className="font-medium text-admin_text">
                {platformDisplay}
              </span> */}
            </div>

            {/* Product ID Display */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm text-admin_text font-mono bg-admin_canvas px-2 py-1 rounded">
                  {displayText}
                </span>

                {/* Validation Status */}
                {isValidating && (
                  <div className="flex items-center gap-1 text-xs text-admin_text_secondary">
                    <div className="animate-spin w-3 h-3 border border-admin_text_secondary border-t-transparent rounded-full"></div>
                  </div>
                )}

                {hasWarning && !isValidating && (
                  <Tooltip
                    content={
                      <div className="max-w-xs">
                        <div className="text-admin_warning font-medium mb-1">
                          {hasWarning.internalDuplicate ? 'Duplicated in this Store Product' : 'Duplicate Product ID'}
                        </div>
                        {hasWarning.conflictingProducts && hasWarning.conflictingProducts.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-admin_bordered">
                            <div className="text-xs font-medium text-admin_text mb-1">
                              {hasWarning.internalDuplicate ? 'Current product:' : 'Used by products:'}
                            </div>
                            <div className="text-xs text-admin_text_secondary">
                              {hasWarning.conflictingProducts.join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    }
                    position="top"
                    display="inline-flex"
                  >
                    <ExclamationTriangleIcon className="w-4 h-4 text-admin_warning cursor-help" />
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="platform-product-editor relative">
      <div className="absolute -top-8 right-0 flex items-center justify-end gap-2">
        <span className="text-sm text-admin_text">
          {localValue.length} {localValue.length === 1 ? "platform" : "platforms"}
        </span>

        <button
          onClick={addNewInput}
          className="w-8 h-8 flex items-center justify-center border-2 border-admin_primary text-admin_primary hover:bg-admin_primary hover:text-white rounded transition-colors"
        >
          <span className="text-2xl leading-none">+</span>
        </button>
      </div>

      <div className="bg-admin_canvas rounded-lg p-4 mt-1">
        {localValue.length > 0 ? (
          localValue.map((item, index) => renderItem(item, index))
        ) : (
          <div className="text-center py-8 text-admin_text_secondary">
            <p>No platform products added yet</p>
          </div>
        )}
      </div>

      {showDialog && (
        <Modal
          isOpen={showDialog}
          onClose={handleCloseDialog}
          closeOnEscape
          variant="admin_centered"
        >
          <div className="p-6" key={selectedIndex !== null ? `edit-${selectedIndex}` : 'new'}>
            {/* Platform and Product Info Header */}
            {itemContentValues.platform && (() => {
              const platformString = typeof itemContentValues.platform === 'object'
                ? itemContentValues.platform.value
                : itemContentValues.platform

              // Get store product name from parent context
              let productName = null
              if (typeof parentValues === 'function') {
                const parentData = parentValues()
                productName = parentData?._parentProduct?.name || parentData?.name
              } else {
                const parentObjectData = typeof parentObject === 'function' ? parentObject() : parentObject
                productName = parentValues?._parentProduct?.name || parentValues?.name || parentObjectData?._parentProduct?.name || parentObjectData?.name
              }

              return (
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-admin_bordered">
                  {/* Platform Icon */}
                  <div className="flex-shrink-0">
                    {renderPlatformIcon(platformString.toLowerCase(), "w-8 h-8 text-admin_text")}
                  </div>
                  {/* Platform and Product Name */}
                  <div className="flex-1">
                    <div className="text-lg font-semibold text-admin_text">
                      {getPlatformDisplayName(platformString)}
                    </div>
                    {productName && (
                      <div className="text-sm text-admin_text_secondary">
                        {productName}
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}

            <div className="space-y-6">
              {/* Platform Cards - Radio Group - Only show when adding new */}
              {selectedIndex === null && (
                <div>
                  <label className="block text-sm font-medium text-admin_text mb-3">Platform</label>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { value: "iOS", label: "iOS" },
                      { value: "Android", label: "Android" },
                      { value: "Web", label: "Web" },
                      { value: "Windows", label: "Windows" }
                    ].map((platform) => {
                      const isSelected = (itemContentValues.platform || "iOS") === platform.value
                      // Find existing item for this platform (excluding current if different platform)
                      const existingItem = localValue.find(item => {
                        const itemPlatform = typeof item.platform === 'object' ? item.platform.value : item.platform
                        const itemIndex = localValue.indexOf(item)

                        // If we're editing an item (selectedIndex is set) and this is the selected item with matching platform
                        if (selectedIndex !== null && itemIndex === selectedIndex && itemPlatform === platform.value) {
                          return true // Show checkmark for the currently edited item
                        }

                        // Otherwise, only show checkmark if there's a different item with this platform
                        return itemPlatform === platform.value && itemIndex !== selectedIndex
                      })
                      const hasExistingValue = !!existingItem

                      return (
                        <button
                          key={platform.value}
                          type="button"
                          onClick={() => {
                            // If there's an existing item for this platform, load its values
                            if (hasExistingValue) {
                              setItemContentValues({ ...existingItem, platform: platform.value })
                            } else {
                              // Clear the id field when switching to a platform without existing value
                              setItemContentValues({ platform: platform.value, id: "" })
                            }
                            setTimeout(() => {
                              productIdInputRef.current?.focus()
                            }, 100)
                          }}
                          className={`relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                            isSelected
                              ? 'border-admin_primary bg-admin_primary bg-opacity-10 shadow-md'
                              : 'border-admin_bordered bg-admin_paper hover:border-admin_text_secondary'
                          }`}
                        >
                          {/* Checkmark icon if platform has existing value */}
                          {hasExistingValue && (
                            <div className="absolute top-2 right-2 bg-admin_primary rounded-full p-1">
                              <CheckIcon className="w-3 h-3 text-white" />
                            </div>
                          )}

                          {renderPlatformIcon(platform.value, `w-8 h-8 ${isSelected ? 'text-admin_primary' : 'text-admin_text_secondary'}`)}
                          <span className={`mt-2 text-sm font-medium ${isSelected ? 'text-admin_primary' : 'text-admin_text'}`}>
                            {platform.label}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Product ID - only if not free */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-admin_text">Product ID</label>
                  {itemContentValues.id && (
                    <Tooltip content="Copy Product ID" position="top">
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(itemContentValues.id)
                          showNotification({
                            message: 'Product ID copied to clipboard',
                            className: 'bg-admin_success text-white'
                          })
                        }}
                        className="p-0.5 hover:bg-admin_neutral_1 rounded transition-colors"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4 text-admin_text_secondary" />
                      </button>
                    </Tooltip>
                  )}
                </div>
                <Input
                    ref={productIdInputRef}
                    value={itemContentValues.id || ""}
                    onChange={(val) => {
                      const newValue = typeof val === 'object' ? val.target?.value : val
                      setItemContentValues({ ...itemContentValues, id: newValue })
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveItem(itemContentValues)
                        handleCloseDialog()
                      }
                    }}
                    placeholder="Enter store product ID here"
                    variant="admin"
                    showClearButton={true}
                    autoFocus
                  />
                  {dialogValidationWarning && (
                    <div className="mt-2 p-3 bg-admin_warning bg-opacity-10 border border-admin_warning rounded-lg">
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-admin_warning flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-admin_warning">
                            {dialogValidationWarning.internalDuplicate ? 'Duplicated in this Store Product' : 'Duplicate Product ID'}
                          </div>
                          {dialogValidationWarning.conflictingProducts && dialogValidationWarning.conflictingProducts.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-admin_bordered">
                              <div className="text-xs font-medium text-admin_text">
                                {dialogValidationWarning.internalDuplicate ? 'Current product:' : 'Used by products:'}
                              </div>
                              <div className="text-xs text-admin_text_secondary mt-1">
                                {dialogValidationWarning.conflictingProducts.join(', ')}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
            </div>

            <div className="flex justify-between items-center mt-8 pt-4 border-t border-admin_bordered">
              {selectedIndex !== null && selectedIndex < localValue.length ? (
                <Button
                  variant="admin_secondary"
                  className="text-admin_error border-admin_error hover:bg-admin_error hover:text-white"
                  confirm={true}
                  confirmMessage={`Remove ${itemContentValues.id || 'this product'} from the list?`}
                  onClick={() => {
                    removeInput(selectedIndex)
                    handleCloseDialog()
                  }}
                >
                  Delete
                </Button>
              ) : (
                <div></div>
              )}
              <button
                onClick={async () => {
                  if (!dialogValidationWarning && !isSaving) {
                    const success = await handleSaveItem(itemContentValues)
                    if (success) {
                      handleCloseDialog()
                    }
                  }
                }}
                disabled={!!dialogValidationWarning || !itemContentValues.id?.trim() || isSaving}
                className={`h-[40px] flex items-center justify-center gap-2 font-bold rounded-full py-2 px-4 transition duration-200 ease-in-out w-[120px] ${
                  dialogValidationWarning || !itemContentValues.id?.trim() || isSaving
                    ? 'bg-admin_neutral_2 text-admin_text_secondary cursor-not-allowed'
                    : 'bg-admin_primary hover:bg-admin_primary_dk text-white'
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default PlatformProductEditor